import {
	Box,
	Button,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	HStack,
	Input,
	Stack,
	Text,
	VStack
} from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";
import EmojiPicker from "emoji-picker-react";

export default function LiveChat({ isOpen, onClose }) {
	const [email, setEmail] = useState("");
	const [emailTouched, setEmailTouched] = useState(false);
	const [chatInput, setChatInput] = useState("");
	const [chatMessages, setChatMessages] = useState([
		{ id: 1, from: "support", text: "Hi! How can we help you today?" }
	]);
	const [hasSentFirstMessage, setHasSentFirstMessage] = useState(false);
	const ws = useRef(null);
	const shouldReconnect = useRef(false);
	const [showEmojiPicker, setShowEmojiPicker] = useState(false);
	const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";
	const WS_BASE_URL = API_BASE_URL.replace(/^http/, "ws");

	const formatTimestamp = (value) => {
		if (!value) return undefined;
		const date = value instanceof Date ? value : new Date(value);
		if (Number.isNaN(date.getTime())) return undefined;
		return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
	};

	const connectWebSocket = (normalizedEmail) => {
		if (ws.current && ws.current.readyState === 1) return;
		ws.current = new window.WebSocket(`${WS_BASE_URL}/ws/livechat/?email=${encodeURIComponent(normalizedEmail)}`);
		ws.current.onopen = () => {};
		ws.current.onmessage = (event) => {
			const data = JSON.parse(event.data);
			setChatMessages((prev) => [
				...prev,
				{
					id: Date.now(),
					from: data.sender === "user" ? "user" : "support",
					text: data.message,
					timestamp: formatTimestamp(data.timestamp) || formatTimestamp(new Date())
				}
			]);
		};
		ws.current.onclose = () => {
			if (shouldReconnect.current) {
				setTimeout(() => connectWebSocket(normalizedEmail), 500);
			}
		};
		ws.current.onerror = () => {
			if (ws.current) {
				ws.current.close();
			}
		};
	};

	// Connect to WebSocket when chat opens and email is valid
	useEffect(() => {
		if (!isOpen || !validateEmail(email)) return;
		const normalizedEmail = email.trim().toLowerCase();
		shouldReconnect.current = true;
		connectWebSocket(normalizedEmail);
		setHasSentFirstMessage(false); // Reset when chat opens
		return () => {
			shouldReconnect.current = false;
			ws.current && ws.current.close();
		};
	}, [isOpen, email]);

	// Fetch chat history when email becomes valid
	useEffect(() => {
		async function fetchHistory() {
			if (validateEmail(email)) {
				try {
					const normalizedEmail = email.trim().toLowerCase();
					const res = await fetch(`${API_BASE_URL}/api/chat_history/?email=${encodeURIComponent(normalizedEmail)}`);
					const data = await res.json();
					if (data.status === 'success') {
						setChatMessages(
							data.messages.map(msg => ({
								id: msg.id,
								from: msg.sender === "user" ? "user" : "support",
								text: msg.message,
								timestamp: formatTimestamp(msg.timestamp)
							}))
						);
					}
				} catch (e) {
					// fallback: do nothing
				}
			} else {
				setChatMessages([{ id: 1, from: "support", text: "Hi! How can we help you today?" }]);
			}
		}
		fetchHistory();
	}, [email]);

	function validateEmail(email) {
		// Simple email regex
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
	}

	const handleEmojiClick = (emojiData) => {
		setChatInput((prev) => prev + emojiData.emoji);
	};

	const handleSendMessage = () => {
		const message = chatInput.trim();
		if (!message || !ws.current || ws.current.readyState !== 1) return;
		if (!validateEmail(email)) return;
		const normalizedEmail = email.trim().toLowerCase();
		const timestamp = formatTimestamp(new Date());
		const payload = { message, sender: "user", email: normalizedEmail, timestamp };
		if (!ws.current || ws.current.readyState !== 1) {
			connectWebSocket(normalizedEmail);
			ws.current.addEventListener(
				"open",
				() => ws.current.send(JSON.stringify(payload)),
				{ once: true }
			);
		} else {
			ws.current.send(JSON.stringify(payload));
		}
		if (!hasSentFirstMessage) {
			setHasSentFirstMessage(true);
		}
		setChatInput("");
	};

	return (
		<Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
			<DrawerOverlay />
			<DrawerContent>
				<DrawerCloseButton />
				<DrawerHeader >Live Chat</DrawerHeader>
				<DrawerBody>
					<VStack spacing={8} align="stretch" mt={-2}>
						<Text color="gray.600">Start a real-time conversation with our support team.</Text>
						<VStack spacing={4} align="stretch">
							{chatMessages.map((message) => (
								<HStack
									key={message.id}
									justify={message.from === "user" ? "flex-end" : "flex-start"}
								>
									<Box
										bg={message.from === "user" ? "gray.700" : "gray.100"}
										color={message.from === "user" ? "white" : "gray.700"}
										px={4}
										py={2}
										borderRadius="lg"
										maxW="75%"
									>
										<Text fontSize="sm" whiteSpace="pre-line">{message.text}</Text>
										{message.timestamp && (
											<Text fontSize="xs" color="gray.400" mt={1} textAlign="right">{message.timestamp}</Text>
										)}
									</Box>
								</HStack>
							))}
						</VStack>
						{!hasSentFirstMessage ? (
							<VStack spacing={3} align="stretch">
								<Input
									placeholder="Enter your email address"
									bg="gray.50"
									value={email}
									onChange={e => setEmail(e.target.value)}
									onBlur={() => {
										setEmailTouched(true);
										if (validateEmail(email)) {
											// Fetch chat history immediately on blur if valid
											(async () => {
												try {
													const normalizedEmail = email.trim().toLowerCase();
													const res = await fetch(`${API_BASE_URL}/api/chat_history/?email=${encodeURIComponent(normalizedEmail)}`);
													const data = await res.json();
													if (data.status === 'success') {
														setChatMessages(
															data.messages.map(msg => ({
																id: msg.id,
																from: msg.sender === "user" ? "user" : "support",
																text: msg.message,
																timestamp: formatTimestamp(msg.timestamp)
															}))
														);
													}
												} catch (e) {}
											})();
										}
									}}
									isInvalid={emailTouched && !validateEmail(email)}
									focusBorderColor="black"
									required
								/>
								<Box position="relative" w="100%">
									<Input
										placeholder="Type your message..."
										bg="gray.50"
										value={chatInput}
										onChange={(event) => setChatInput(event.target.value)}
										onKeyDown={(event) => {
											if (event.key === "Enter") {
												event.preventDefault();
												handleSendMessage();
											}
										}}
										isDisabled={!validateEmail(email)}
										focusBorderColor="black"
									/>
									<Button
										size="sm"
										position="absolute"
										right="2px"
										top="2px"
										zIndex={2}
										onClick={() => setShowEmojiPicker((v) => !v)}
									>
										😊
									</Button>
									{showEmojiPicker && (
										<Box position="absolute" zIndex={3} top="40px" right="0">
											<EmojiPicker onEmojiClick={handleEmojiClick} height={350} width={300} />
										</Box>
									)}
								</Box>
								<Button
									bg="black"
									color="white"
									_hover={{ bg: "gray.800" }}
									onClick={handleSendMessage}
									isDisabled={!validateEmail(email) || !chatInput.trim()}
								>
									Send
								</Button>
							</VStack>
						) : (
							<Stack direction={{ base: "column", md: "row" }} spacing={3}>
								<Box position="relative" w="100%">
									<Input
										placeholder="Type your message..."
										bg="gray.50"
										value={chatInput}
										onChange={(event) => setChatInput(event.target.value)}
										onKeyDown={(event) => {
											if (event.key === "Enter") {
												event.preventDefault();
												handleSendMessage();
											}
										}}
										focusBorderColor="black"
									/>
									<Button
										size="sm"
										position="absolute"
										right="2px"
										top="2px"
										zIndex={2}
										onClick={() => setShowEmojiPicker((v) => !v)}
									>
										😊
									</Button>
									{showEmojiPicker && (
										<Box position="absolute" zIndex={3} top="40px" right="0">
											<EmojiPicker onEmojiClick={handleEmojiClick} height={350} width={300} />
										</Box>
									)}
								</Box>
								<Button
									bg="black"
									color="white"
									_hover={{ bg: "gray.800" }}
									onClick={handleSendMessage}
									isDisabled={!chatInput.trim()}
								>
									Send
								</Button>
							</Stack>
						)}
					</VStack>
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
}